/**
 * Bundle Builder – Frontend Logic
 * Bonbons: server API | Styling: CSS via Liquid style blocks
 */

(function () {
  'use strict';

  let boxes = [];
  let bonbons = [];
  let selectedBox = null;
  let selections = [];

  const $ = id => document.getElementById(id);

  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function formatPrice(p) {
    return parseFloat(p).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  function showToast(msg) {
    const el = document.createElement('div');
    el.className = 'bb-toast'; el.textContent = msg;
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 3500);
  }

  function getWrapper() { return $('bundle-builder'); }
  function getServerUrl() {
    const w = getWrapper();
    return w ? (w.dataset.serverUrl || '').replace(/\/$/, '') : '';
  }
  function serverFetch(endpoint) {
    const url = getServerUrl();
    if (!url) return Promise.reject(new Error('Geen server URL'));
    return fetch(`${url}${endpoint}`, {
      headers: { 'ngrok-skip-browser-warning': '1', 'Accept': 'application/json' }
    });
  }

  // ─── Block config lezen ────────────────────────────────────────────────────
  function getBtnInfo() {
    const b = document.querySelector('[data-block="button"]');
    return {
      label: b?.dataset.label || 'Voeg toe',
      plusIcon: b?.dataset.plusIcon || '',
      minusIcon: b?.dataset.minusIcon || '',
    };
  }

  function iconHtml(src, alt) {
    return src ? `<img src="${escHtml(src)}" alt="${alt}" style="width:20px;height:20px;object-fit:contain;display:block">` : alt;
  }

  // ─── Storefront API: metafields ophalen ───────────────────────────────────────
  async function loadLabels(productIds) {
    const token = window.__bbStorefrontToken;
    if (!token || !productIds.length) return;
    const gids = productIds.map(id => `"gid://shopify/Product/${id}"`);
    const query = `{
      nodes(ids: [${gids.join(',')}]) {
        ... on Product {
          id
          metafield(namespace: "custom", key: "bundle_builder_label") {
            value
          }
        }
      }
    }`;
    try {
      const res = await fetch('/api/2024-01/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token
        },
        body: JSON.stringify({ query })
      });
      if (!res.ok) return;
      const json = await res.json();
      const nodes = json?.data?.nodes || [];
      window.__bbLabels = {};
      for (const node of nodes) {
        if (node?.metafield?.value) {
          const numericId = node.id.replace('gid://shopify/Product/', '');
          window.__bbLabels[numericId] = node.metafield.value;
        }
      }
    } catch (e) {
      console.warn('[BundleBuilder] Kon labels niet laden:', e);
    }
  }

  // ─── Init ──────────────────────────────────────────────────────────────────
  async function init() {
    if (!getWrapper() || !getServerUrl()) {
      console.warn('[BundleBuilder] Geen server URL ingesteld.');
      return;
    }
    try {
      const [boxRes, bonbonRes] = await Promise.all([
        serverFetch('/api/boxes'),
        serverFetch('/api/bonbons'),
      ]);
      boxes = await boxRes.json();
      bonbons = await bonbonRes.json();

      // Haal labels op via Storefront API (parallel, non-blocking voor render)
      const productIds = bonbons.map(b => b.productId).filter(Boolean);
      await loadLabels(productIds);

      renderBoxSelector();
      renderBonbons();
      // Auto-selecteer de kleinste doos
      if (boxes.length) {
        const sorted = [...boxes].sort((a, b) => a.capacity - b.capacity);
        selectBox(sorted[0].id);
      } else {
        renderSlots();
        updateUI();
      }
    } catch (e) {
      const grid = $('bb-bonbon-grid');
      if (grid) grid.innerHTML = `<div class="bb-loading"><p>⚠️ Kon bonbons niet laden. Controleer de server URL.</p></div>`;
      console.error('[BundleBuilder]', e);
    }
  }

  // ─── Box selector ──────────────────────────────────────────────────────────
  function renderBoxSelector() {
    const el = $('bb-box-selector');
    if (!el) return;
    if (!boxes.length) {
      el.innerHTML = `<p style="font-size:13px;color:#888">Geen doosformaten gevonden. Controleer de server URL en admin.</p>`;
      return;
    }
    el.innerHTML = boxes
      .sort((a, b) => a.capacity - b.capacity)
      .map(box => `
        <button class="bb-box-btn" data-box-id="${box.id}"
          onclick="window.__bb.selectBox('${box.id}')">
          ${escHtml(box.name)}
          ${box.label ? `<span class="bb-box-label">${escHtml(box.label)}</span>` : ''}
        </button>`).join('');
  }

  // ─── Bonbon cards ──────────────────────────────────────────────────────────
  // Nieuwe structuur: top-rij (badge + i-knop) → afbeelding → naam → knop
  function renderBonbons() {
    const grid = $('bb-bonbon-grid');
    if (!grid) return;
    if (!bonbons.length) {
      grid.innerHTML = `<div class="bb-loading"><p>Geen bonbons beschikbaar.</p></div>`;
      return;
    }

    const btn = getBtnInfo();

    // SVG decoratieve rand rechts van het label (ticket-stijl)
    const svgEdge = `<svg class="bb-label-edge" viewBox="0 0 4 20" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg"><path d="M0.648347 5.72349C0.648347 7.06741 1.73465 8.15371 3.07857 8.15371V11.447C1.73465 11.447 0.648347 12.5376 0.648347 13.8772C0.648347 15.2168 1.73465 16.3074 3.07857 16.3074V20H0V0H3.07857V3.29326C1.73465 3.29326 0.648347 4.37956 0.648347 5.72349Z" fill="white"/></svg>`;

    grid.innerHTML = bonbons.map(b => {
      // b.label komt direct van de server API (via custom.bundle_builder_label metafield)
      const bbLabel = b.label || '';
      const imgHtml = b.image
        ? `<img src="${b.image}" alt="${escHtml(b.name)}" loading="lazy" />`
        : `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;font-size:40px">🍫</div>`;

      return `
        <div class="bb-card" id="bb-card-${b.id}" onclick="window.__bb.addBonbon('${b.id}')">
          <div class="bb-card-img-wrap">
            ${imgHtml}
            ${bbLabel ? `<div class="bb-card-img-label"><span class="bb-label-text">${escHtml(bbLabel)}</span>${svgEdge}</div>` : ''}
            <button class="bb-card-info-btn" onclick="event.stopPropagation()" title="Info" aria-label="Info over ${escHtml(b.name)}">i</button>
          </div>
          <div class="bb-card-body">
            <div class="bb-card-name">${escHtml(b.name)}</div>
            <div class="bb-card-action" id="bb-action-${b.id}" onclick="event.stopPropagation()">
              <button class="bb-add-btn" onclick="window.__bb.addBonbon('${b.id}')"
                ${!selectedBox ? 'disabled' : ''}>
                ${escHtml(btn.label)} <span>+</span>
              </button>
            </div>
          </div>
        </div>`;
    }).join('');
  }

  // ─── Slots (rechter paneel) ────────────────────────────────────────────────
  function renderSlots() {
    const el = $('bb-slots');
    if (!el) return;
    if (!selectedBox) {
      el.innerHTML = `<div style="grid-column:1/-1;color:#bbb;font-size:13px;padding:8px 0">Kies eerst een doosformaat</div>`;
      return;
    }
    const capacity = selectedBox.capacity;
    let html = '';
    for (let i = 0; i < capacity; i++) {
      const bonbon = selections[i];
      if (bonbon) {
        const bgStyle = bonbon.slotColor
          ? `background:${bonbon.slotColor};`
          : bonbon.image
            ? `background-image:url('${bonbon.image}');background-size:cover;background-position:center;`
            : `background:#6b4226;`;
        html += `
          <div class="bb-slot">
            <div class="bb-slot-oval filled" style="${bgStyle}">
              <button class="bb-slot-remove" onclick="window.__bb.removeSlot(${i})" title="Verwijder">✕</button>
            </div>
            <div class="bb-slot-name">${escHtml(bonbon.name)}</div>
          </div>`;
      } else {
        html += `<div class="bb-slot"><div class="bb-slot-oval"></div><div class="bb-slot-name"></div></div>`;
      }
    }
    el.innerHTML = html;
    // Stel alle ovals op dezelfde hoogte in (JS-gedreven, immuun voor theme CSS)
    requestAnimationFrame(normalizeSlotSizes);
  }

  function normalizeSlotSizes() {
    const grid = $('bb-slots');
    if (!grid) return;
    const ovals = grid.querySelectorAll('.bb-slot-oval');
    if (!ovals.length) return;
    ovals.forEach(el => { el.style.height = '100px'; });
  }


  // ─── Update UI ─────────────────────────────────────────────────────────────
  function updateUI() {
    const count = selections.length;
    const capacity = selectedBox?.capacity || 0;
    const boxFull = selectedBox && count >= capacity;
    const btn = getBtnInfo();

    const countEl = $('bb-count');
    const capacityEl = $('bb-capacity');
    const selectedLabel = $('bb-selected-label');
    const priceEl = $('bb-price-display');
    const ctaBtn = $('bb-add-to-cart');

    if (countEl) countEl.textContent = count;
    if (capacityEl) capacityEl.textContent = selectedBox ? capacity : '?';
    if (selectedLabel) selectedLabel.textContent = selectedBox ? selectedBox.name : 'kies een formaat';
    if (priceEl) {
      priceEl.innerHTML = selectedBox
        ? `<span class="bb-price">€ ${formatPrice(selectedBox.price)}</span>`
        : `<span class="bb-price">€ –</span>`;
    }

    document.querySelectorAll('.bb-box-btn').forEach(el => {
      el.classList.toggle('active', el.dataset.boxId === selectedBox?.id);
    });

    // Per-bonbon actie updaten
    bonbons.forEach(b => {
      const countForBonbon = selections.filter(s => s.id === b.id).length;
      const actionEl = $(`bb-action-${b.id}`);
      if (!actionEl) return;

      if (countForBonbon > 0) {
        // Grote pill-counter knoppen
        actionEl.innerHTML = `
          <div class="bb-counter">
            <button class="bb-counter-btn" onclick="window.__bb.decBonbon('${b.id}')">
              ${iconHtml(btn.minusIcon, '−')}
            </button>
            <span class="bb-counter-val">${countForBonbon}</span>
            <button class="bb-counter-btn" onclick="window.__bb.addBonbon('${b.id}')" ${boxFull ? 'disabled' : ''}>
              ${iconHtml(btn.plusIcon, '+')}
            </button>
          </div>`;
      } else {
        actionEl.innerHTML = `
          <button class="bb-add-btn" onclick="window.__bb.addBonbon('${b.id}')"
            ${!selectedBox || boxFull ? 'disabled' : ''}>
            ${escHtml(btn.label)} <span>${iconHtml(btn.plusIcon, '+')}</span>
          </button>`;
      }
    });

    if (ctaBtn) {
      const isFull = selectedBox && count === capacity;
      ctaBtn.disabled = !isFull;
      ctaBtn.classList.toggle('ready', !!isFull);
      const ctaDef = ctaBtn.dataset.defaultText || 'Vul je doos';
      const ctaIconUrl = ctaBtn.dataset.customIconUrl;
      const ctaIconHtml = ctaIconUrl
        ? `<img src="${ctaIconUrl}" class="bb-cta-icon-img" alt="">`
        : `<span class="bb-cta-icon">\uD83D\uDED2</span>`;
      ctaBtn.innerHTML = isFull
        ? `${ctaIconHtml} In winkelwagen`
        : `${ctaIconHtml} ${ctaDef}`;
    }

    renderSlots();
    syncMobileFooter();
  }

  // ─── Mobile Footer ─────────────────────────────────────────────────────────
  function toggleMobileFooter() {
    const footer = document.getElementById('bb-mobile-footer');
    if (!footer) return;
    const isOpen = footer.classList.toggle('bb-mf-open');
    const title = document.getElementById('bb-mf-title');
    const arrow = document.getElementById('bb-mf-arrow');
    if (title) title.textContent = isOpen ? 'Sluiten' : 'Openen';
    if (arrow) arrow.textContent = isOpen ? '\u2228' : '\u2227';
  }

  function syncMobileFooter() {
    const el = document.getElementById('bb-mf-slots');
    const ctaBtn = document.getElementById('bb-mf-cta-btn');
    if (!el) return;
    if (!selectedBox) {
      el.innerHTML = `<div style="grid-column:1/-1;color:#bbb;font-size:13px;padding:8px 0">Kies eerst een doosformaat</div>`;
    } else {
      const capacity = selectedBox.capacity;
      let html = '';
      for (let i = 0; i < capacity; i++) {
        const bonbon = selections[i];
        if (bonbon) {
          const bgStyle = bonbon.slotColor
            ? `background:${bonbon.slotColor};`
            : bonbon.image
              ? `background-image:url('${bonbon.image}');background-size:cover;background-position:center;`
              : `background:#6b4226;`;
          html += `
            <div class="bb-slot" style="width:60px;min-width:60px;flex-shrink:0;">
              <div class="bb-slot-oval filled" style="width:36px;height:78px;${bgStyle}">
                <button class="bb-slot-remove" onclick="window.__bb.removeSlot(${i})" title="Verwijder">\u2715</button>
              </div>
              <div class="bb-slot-name">${escHtml(bonbon.name)}</div>
            </div>`;
        } else {
          html += `<div class="bb-slot" style="width:60px;min-width:60px;flex-shrink:0;"><div class="bb-slot-oval" style="width:36px;height:78px;"></div><div class="bb-slot-name"></div></div>`;
        }
      }
      el.innerHTML = html;
    }
    if (ctaBtn) {
      const count = selections.length;
      const isFull = selectedBox && count === selectedBox.capacity;
      ctaBtn.disabled = !isFull;
      ctaBtn.classList.toggle('ready', !!isFull);
      const def = ctaBtn.dataset.defaultText || 'Vul je doos';
      const iconUrl = ctaBtn.dataset.customIconUrl;
      const iconHtml = iconUrl
        ? `<img src="${iconUrl}" class="bb-cta-icon-img" alt="">`
        : `<span class="bb-cta-icon">\uD83D\uDED2</span>`;
      ctaBtn.innerHTML = isFull
        ? `${iconHtml} In winkelwagen`
        : `${iconHtml} ${def}`;
    }
  }

  function selectBox(id) {
    const newBox = boxes.find(b => b.id === id) || null;
    if (!newBox) return;
    selectedBox = newBox;
    // Bewaar selecties — trim naar nieuwe capaciteit (eerste N items)
    selections = selections.slice(0, newBox.capacity);
    updateUI();
  }
  function addBonbon(id) {
    if (!selectedBox || selections.length >= selectedBox.capacity) return;
    const bonbon = bonbons.find(b => b.id === id);
    if (!bonbon) return;
    selections.push(bonbon);
    updateUI();
  }
  function decBonbon(id) {
    const idx = selections.map(s => s.id).lastIndexOf(id);
    if (idx === -1) return;
    selections.splice(idx, 1);
    updateUI();
  }
  function removeSlot(index) {
    selections.splice(index, 1);
    updateUI();
  }

  // ─── Add to Cart ───────────────────────────────────────────────────────────
  async function addToCart() {
    if (!selectedBox || selections.length !== selectedBox.capacity) return;
    if (!selectedBox.variantId) {
      showToast('⚠️ Geen Shopify variant ingesteld voor dit doosformaat.');
      return;
    }
    const ctaBtn = $('bb-add-to-cart');
    if (ctaBtn) { ctaBtn.classList.add('loading'); ctaBtn.textContent = 'Toevoegen...'; }

    const properties = {};
    selections.forEach((s, i) => { properties[`Bonbon ${i + 1}`] = s.name; });
    const summary = [...new Map(selections.map(s => [s.name, s])).values()]
      .map(s => { const q = selections.filter(x => x.id === s.id).length; return q > 1 ? `${q}× ${s.name}` : s.name; })
      .join(', ');
    properties['Samenstelling'] = summary;

    const cartItemsComponents = Array.from(document.querySelectorAll('cart-items-component'));
    const sections = cartItemsComponents.map(el => el.dataset.sectionId).filter(Boolean);

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          id: selectedBox.variantId, 
          quantity: 1, 
          properties,
          sections: sections.join(',')
        }),
      });
      if (res.ok) {
        const responseData = await res.json();
        
        showToast('🎉 Bundel toegevoegd aan je winkelwagen!');
        selections = [];
        updateUI();
        
        // Dispatch cart update so the theme's drawer catches it
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            sourceId: 'bundle-builder',
            data: {
              source: 'product-form-component', 
              itemCount: 1,
              sections: responseData.sections
            }
          }
        }));

        setTimeout(() => document.dispatchEvent(new CustomEvent('bb:added-to-cart')), 400);
      } else {
        const err = await res.json();
        showToast(`⚠️ ${err.description || 'Er ging iets mis.'}`);
      }
    } catch { showToast('⚠️ Verbindingsfout. Probeer opnieuw.'); }

    if (ctaBtn) {
      ctaBtn.classList.remove('loading');
      ctaBtn.innerHTML = `<span class="bb-cta-icon">🛒</span> ${getWrapper()?.dataset.ctaText || 'Vul je doos'}`;
    }
  }

  // ─── Events ────────────────────────────────────────────────────────────────
  document.addEventListener('click', e => {
    if (e.target.closest('#bb-add-to-cart')) addToCart();
    if (e.target.closest('#bb-mf-cta-btn')) {
      // Delegate to the desktop button so the upsell popup intercept fires the same way
      const desktopBtn = document.getElementById('bb-add-to-cart');
      if (desktopBtn && !desktopBtn.disabled) {
        desktopBtn.click();
      } else {
        addToCart();
      }
    }
  });
  document.addEventListener('shopify:section:load', () => {
    if (!$('bundle-builder')) return;
    renderBonbons();
    updateUI();
  });
  window.addEventListener('resize', normalizeSlotSizes);

  window.__bb = { selectBox, addBonbon, decBonbon, removeSlot, toggleMobileFooter, addToCart };
  init();

})();
